/**
 * Roadmap-Aware Forecast — admin page.
 *
 * Lets users:
 *   1. Add roadmap events (manual entry; Jira/Linear adapters coming)
 *   2. See the Monte Carlo forecast with confidence-banded fan chart
 *   3. Drag the slider to change horizon
 *
 * The chart shows P5/P50/P95 bands with named event markers — no other
 * FinOps tool does this at the SaaS feature level.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calendar, Sparkles, Plus, Trash2, TrendingUp } from 'lucide-react';
import WebhookSetupWizard from '../../components/WebhookSetupWizard';
import BrandTitle from '../../components/BrandTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const Fan = ({ data, eventMarkers = [] }) => {
  // SVG fan chart with P5/P50/P95
  if (!data || !data.bands || data.bands.length === 0) return null;
  const W = 880;
  const H = 280;
  const PAD = { l: 60, r: 20, t: 16, b: 36 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const bands = data.bands;
  const xs = bands.map((_, i) => PAD.l + (i / Math.max(1, bands.length - 1)) * innerW);
  const allVals = bands.flatMap((b) => [b.p5, b.p50, b.p95]);
  const yMin = Math.min(...allVals) * 0.9;
  const yMax = Math.max(...allVals) * 1.05;
  const yScale = (v) => PAD.t + (1 - (v - yMin) / Math.max(0.01, yMax - yMin)) * innerH;
  // P5/P95 area path (the fan)
  const upper = bands.map((b, i) => `${i === 0 ? 'M' : 'L'} ${xs[i].toFixed(1)} ${yScale(b.p95).toFixed(1)}`).join(' ');
  const lower = bands.slice().reverse().map((b, i) => `L ${xs[bands.length - 1 - i].toFixed(1)} ${yScale(b.p5).toFixed(1)}`).join(' ');
  const fanPath = `${upper} ${lower} Z`;
  // P50 line
  const p50Path = bands.map((b, i) => `${i === 0 ? 'M' : 'L'} ${xs[i].toFixed(1)} ${yScale(b.p50).toFixed(1)}`).join(' ');

  // X-axis ticks: first / mid / last
  const xTicks = [0, Math.floor(bands.length / 2), bands.length - 1].filter((v, i, a) => a.indexOf(v) === i);

  // Map event markers to chart x position
  const markerLines = eventMarkers.map((m) => {
    const idx = bands.findIndex((b) => b.date >= m.date);
    if (idx === -1) return null;
    return {
      x: xs[idx],
      label: m.label,
      monthlyDelta: m.monthly_delta_usd,
    };
  }).filter(Boolean);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" aria-label="Forecast fan chart" data-testid="roadmap-forecast-chart">
      <defs>
        <linearGradient id="fan-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      {/* Grid */}
      {[0.25, 0.5, 0.75].map((p) => (
        <line key={p} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + p * innerH} y2={PAD.t + p * innerH}
              stroke="#1f2937" strokeWidth="0.5" strokeDasharray="3 3" />
      ))}
      {/* Fan */}
      <path d={fanPath} fill="url(#fan-grad)" />
      {/* P50 line */}
      <path d={p50Path} fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" />
      {/* Event markers */}
      {markerLines.map((m, i) => (
        <g key={i}>
          <line x1={m.x} x2={m.x} y1={PAD.t} y2={H - PAD.b}
                stroke="#fbbf24" strokeWidth="1.2" strokeDasharray="2 3" />
          <text x={m.x} y={PAD.t - 4} fill="#fbbf24" fontSize="10" textAnchor="middle">
            {m.label} (+${(m.monthlyDelta / 1000).toFixed(1)}K/mo)
          </text>
        </g>
      ))}
      {/* X-axis ticks */}
      {xTicks.map((i) => (
        <text key={i} x={xs[i]} y={H - 12} fill="#64748b" fontSize="10" textAnchor="middle">
          {bands[i].date}
        </text>
      ))}
      {/* Y-axis */}
      <text x={10} y={PAD.t + 6} fill="#64748b" fontSize="10">${(yMax / 1000).toFixed(1)}K</text>
      <text x={10} y={H - PAD.b} fill="#64748b" fontSize="10">${(yMin / 1000).toFixed(1)}K</text>
    </svg>
  );
};

export default function RoadmapForecast() {
  const [events, setEvents] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(90);
  const [newEvent, setNewEvent] = useState({
    date: '', label: '', delta_customers: 0, revenue_per_customer_monthly_usd: 0, delta_usd_monthly: 0,
  });

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const [evRes, fcRes] = await Promise.all([
        fetch(`${API}/api/roadmap-forecast/events`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/roadmap-forecast/forecast`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ horizon_days: horizon, monte_carlo_trials: 500 }),
        }),
      ]);
      setEvents(await evRes.json());
      setForecast(await fcRes.json());
    } finally { setLoading(false); }
  }, [horizon]);

  useEffect(() => { refresh(); }, [refresh]);

  const addEvent = async () => {
    if (!newEvent.date || !newEvent.label) return;
    const token = localStorage.getItem('token');
    const payload = { ...newEvent };
    if (!payload.delta_usd_monthly && !payload.delta_customers) return;
    await fetch(`${API}/api/roadmap-forecast/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    setNewEvent({ date: '', label: '', delta_customers: 0, revenue_per_customer_monthly_usd: 0, delta_usd_monthly: 0 });
    refresh();
  };

  const deleteEvent = async (id) => {
    const token = localStorage.getItem('token');
    await fetch(`${API}/api/roadmap-forecast/events/${id}`, { method: 'DELETE',
                                                              headers: { Authorization: `Bearer ${token}` } });
    refresh();
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="roadmap-forecast-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="inline-flex w-12 h-12 rounded-xl bg-cyan-500 bg-gradient-to-br from-cyan-500 to-emerald-500 items-center justify-center shadow-lg shadow-cyan-500/30">
            <TrendingUp className="w-6 h-6 text-white drop-shadow-sm" />
          </div>
          <div>
            <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono tracking-widest mb-1">WORLD-FIRST</Badge>
            <h1 className="text-3xl font-bold text-slate-900"><BrandTitle>Roadmap-Aware Forecast</BrandTitle></h1>
          </div>
        </div>
        <p className="text-base text-slate-700 max-w-3xl leading-relaxed">
          Inject your launches, expansions, or customer cohorts and see the fan-banded Monte Carlo forecast. Nobody else does forecasting at this fidelity — Anodot does scenarios at infra-level only.
        </p>
      </div>

      {/* Fan chart */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20 mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              90-day forecast · {forecast?.trials || 500}-trial Monte Carlo
            </CardTitle>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <label htmlFor="horizon-slider">Horizon:</label>
              <input
                id="horizon-slider"
                type="range" min="14" max="180" step="7"
                value={horizon}
                onChange={(e) => setHorizon(Number(e.target.value))}
                className="w-32 accent-cyan-400"
                data-testid="horizon-slider"
              />
              <span className="tabular-nums w-12 text-cyan-300">{horizon}d</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-72 bg-slate-800 rounded-lg animate-pulse" />
          ) : (
            <>
              <Fan data={forecast} eventMarkers={forecast?.event_markers || []} />
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-3 h-2 rounded-sm" style={{ background: '#22d3ee', opacity: 0.25 }} />
                  P5–P95 band
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-4 h-0.5 bg-cyan-400" />
                  P50 (median)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-3 h-2.5 border border-amber-400 border-dashed" />
                  Event marker
                </span>
                {forecast?.history_is_synthetic && (
                  <span className="ml-auto text-amber-400/80 text-[11px]">
                    ⚠️ Synthetic baseline · connect cloud account for real history
                  </span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Add event */}
        <Card className="bg-slate-950 text-slate-100 border-emerald-500/20" data-testid="add-event-card">
          <CardHeader><CardTitle className="text-lg text-white flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-400" />Add a roadmap event</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Event date</label>
                  <input type="date" value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Direct $/mo impact</label>
                  <input type="number" placeholder="8000" value={newEvent.delta_usd_monthly || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, delta_usd_monthly: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Label</label>
                <input type="text" placeholder="AI Studio launch · +200 customers" value={newEvent.label}
                  onChange={(e) => setNewEvent({ ...newEvent, label: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <Button onClick={addEvent} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="add-event-btn">
                Add event
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Event list */}
        <Card className="bg-slate-950 text-slate-100 border-emerald-500/20" data-testid="event-list-card">
          <CardHeader><CardTitle className="text-lg text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-400" />Upcoming events ({events.length})</CardTitle></CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No events yet. Add a launch / expansion / cohort to see how the forecast fan changes.
              </div>
            ) : (
              <div className="space-y-2">
                {events.map((ev) => (
                  <div key={ev.id} className="flex items-start gap-3 rounded-md border border-emerald-500/20 bg-slate-950 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{ev.label}</div>
                      <div className="text-xs text-slate-400">
                        {ev.date} · +${(ev.delta_usd_monthly || (ev.delta_customers || 0) * (ev.revenue_per_customer_monthly_usd || 0)).toLocaleString()}/mo
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteEvent(ev.id)} className="h-8 px-2 text-rose-400 hover:text-rose-300">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Webhook setup wizards — Jira + Linear */}
        <div className="space-y-4">
          <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">
            Auto-import launch events
          </div>
          <WebhookSetupWizard source="jira" />
          <WebhookSetupWizard source="linear" />
        </div>
      </div>
      </div>
    </div>
  );
}
